/**
 * Web Bluetooth ELM327 driver.
 *
 * Reference:
 *   docs/superpowers/plans/2026-04-27-vibe-network-mvp.md (Chunk 7 Task 7.7)
 *
 * Targets the common BLE-emulated ELM327 dongles built around the HM-10
 * BLE module (service 0xFFE0, write+notify on 0xFFE1). Classic-Bluetooth
 * SPP dongles aren't reachable from Web Bluetooth — those are wrapped via
 * the Capacitor BT Classic plugin in a follow-up.
 *
 * Browser support:
 *   - Chrome / Edge on Android: full support
 *   - iOS Safari / Chrome: NOT supported (Web Bluetooth disabled)
 *
 * iOS users go through the Capacitor wrap, where this same module is
 * shimmed by a native bridge. The state machine + parser layers are
 * platform-agnostic.
 */

import {
    INIT_COMMANDS,
    PID_QUERIES,
    cleanLine,
    parseLine,
    type ParseResult,
    type Pid,
} from './obd-parser';

/**
 * Standard HM-10 BLE service + characteristic UUIDs used by clone ELM327
 * dongles. Override via constructor for unusual hardware.
 */
export const HM10_SERVICE_UUID = '0000ffe0-0000-1000-8000-00805f9b34fb';
export const HM10_CHAR_UUID    = '0000ffe1-0000-1000-8000-00805f9b34fb';

/** Nordic UART Service — split TX/RX architecture. */
export const NUS_SERVICE_UUID  = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
/** NUS RX char: app writes commands here (device receives). */
export const NUS_RX_CHAR_UUID  = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';
/** NUS TX char: device sends responses here (app subscribes for notify). */
export const NUS_TX_CHAR_UUID  = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';

/**
 * Microchip / ISSC Transparent UART Service — another split TX/RX protocol,
 * used by Nexas and other OBD dongles built on Microchip RN4020 / ISSC BLE chips.
 */
export const ISSC_SERVICE_UUID = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
/** ISSC RX char: app writes OBD commands here. */
export const ISSC_RX_CHAR_UUID = '49535343-6daa-4d02-abf6-19569aca69fe';
/** ISSC TX char: device sends responses here (notify). */
export const ISSC_TX_CHAR_UUID = '49535343-aca3-481c-91ec-d85e28a60318';

/**
 * FFF0 split-UART service — used by Vgate, iCar, and many Nexas/clone dongles.
 * FFF2 is write (AT commands go here), FFF1 is notify (ELM327 responses come back here).
 */
export const FFF0_SERVICE_UUID = '0000fff0-0000-1000-8000-00805f9b34fb';
/** FFF0 RX char: app writes AT commands here (writeWithoutResponse). */
export const FFF0_RX_CHAR_UUID = '0000fff2-0000-1000-8000-00805f9b34fb';
/** FFF0 TX char: device sends ELM327 responses here (notify/indicate). */
export const FFF0_TX_CHAR_UUID = '0000fff1-0000-1000-8000-00805f9b34fb';

/**
 * Other common BLE service UUIDs used by ELM327 clones. We list these as
 * `optionalServices` so we can probe for them after connect, and we
 * iterate them in discoverObdService() to pick whichever the device
 * actually exposes.
 *
 * Discovered the hard way — different clone manufacturers picked
 * different short UUIDs in the proprietary 0xFFxx range. The discovery
 * loop tries known UUIDs first, then enumerates all readable services
 * if none match.
 */
export const ALT_SERVICE_UUIDS = [
    '0000fff0-0000-1000-8000-00805f9b34fb',   // Vgate / VEEPEAK / iCar BLE clones
    '0000ffb0-0000-1000-8000-00805f9b34fb',   // legacy "OBDII Bluetooth 4.0" clones
    '0000ffc0-0000-1000-8000-00805f9b34fb',   // some Topdon / Konnwei BLE
    '0000ffa0-0000-1000-8000-00805f9b34fb',   // some no-name AliExpress clones
    '0000fee0-0000-1000-8000-00805f9b34fb',   // some Foseal / Topdon UCAR
    '0000180f-0000-1000-8000-00805f9b34fb',   // Battery Service — often advertised
    // Nordic UART Service (NUS) — used by a small but growing set of OBD clones
    '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
    // Microchip / ISSC Transparent UART — Nexas ELM327 and other RN4020-based dongles
    '49535343-fe7d-4ae5-8fa9-9fafd205e455',
] as const;

/**
 * Characteristics within OBD-clone services that handle the actual
 * read/write of ELM327 commands. Each service has a paired characteristic
 * 1 short-UUID up from the service (FFE0 → FFE1, FFF0 → FFF1, ...).
 */
export const ALT_CHAR_UUIDS = [
    '0000fff1-0000-1000-8000-00805f9b34fb',   // Vgate / VEEPEAK paired
    '0000ffb1-0000-1000-8000-00805f9b34fb',   // legacy clones paired
    '0000ffc1-0000-1000-8000-00805f9b34fb',
    '0000ffa1-0000-1000-8000-00805f9b34fb',
    '0000fee1-0000-1000-8000-00805f9b34fb',
    // NUS TX/RX
    '6e400002-b5a3-f393-e0a9-e50e24dcca9e',
    '6e400003-b5a3-f393-e0a9-e50e24dcca9e',
] as const;

/**
 * Common name prefixes BLE ELM327 dongles broadcast with. Used as a
 * fallback filter when the dongle does NOT advertise its GATT service
 * UUID (most clones don't — they only expose it after connect).
 */
export const ELM327_NAME_PREFIXES = [
    'OBD',         // covers OBDII, OBD2, OBD-II, OBD2-...
    'V-LINK', 'V-Link', 'Vgate',
    'VEEPEAK', 'Veepeak',
    'ELM327', 'ELM',
    'ICAR', 'iCar',
    'BLE_OBD', 'BLE-OBD',
    'IOS-Vlink',   // VL-100 style
    'IOS-Vcar',
    'OBDLINK',     // Scantool OBDLink
    'BT-ELM',
    'Konnwei', 'KONNWEI',
    'Tonwon', 'TONWON',
    'LELink',
    'Viecar',
    'Carista',
    'NEXAS',       // Nexas NexLink BLE dongles
];

export interface Elm327Options {
    serviceUuid?: string;
    characteristicUuid?: string;
    /** Cap on lines we'll buffer before forcing a parse. */
    lineBufferMax?: number;
    /** Per-PID poll interval in ms. */
    pollIntervalMs?: number;
    /**
     * If true, the BLE chooser will offer ALL nearby devices (not just
     * filtered ELM327 candidates). Useful for diagnostic / unfamiliar
     * hardware where the namePrefix filter list misses the brand.
     * Trade-off: scarier consent dialog for the user.
     */
    acceptAllDevices?: boolean;
}

/**
 * Whether the host supports Web Bluetooth at all. Used by the UI to
 * gate the Connect button + show a friendly message on iOS.
 */
export function isWebBluetoothSupported(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as Navigator & {
        bluetooth?: unknown;
    }).bluetooth;
}

export type DriverEvent =
    | { kind: 'status'; status: 'idle' | 'requesting' | 'connecting' | 'initialising' | 'connected' | 'disconnected' | 'error'; message?: string }
    | { kind: 'line'; raw: string }
    | { kind: 'parsed'; result: ParseResult };

/**
 * Transport-agnostic interface used by Elm327Driver. Two implementations:
 * - WebBluetoothTransport (Chrome on Android — works in the PWA)
 * - NativeBleTransport (Capacitor on iOS + Android native shells)
 */
export interface BleTransport {
    write(bytes: Uint8Array): Promise<void>;
    onNotify(handler: (bytes: Uint8Array) => void): void;
    onDisconnect(handler: () => void): void;
    disconnect(): Promise<void>;
}

export type TransportFactory = (opts: Required<Elm327Options>, emit: (e: DriverEvent) => void) => Promise<BleTransport>;

export class Elm327Driver {
    private transport: BleTransport | null = null;
    private listeners: Array<(e: DriverEvent) => void> = [];
    private buffer = '';
    private pollHandle: ReturnType<typeof setInterval> | null = null;
    private pidCursor = 0;
    private readonly pids: Pid[] = ['speed', 'rpm', 'throttle'];
    private readonly opts: Required<Elm327Options>;
    private readonly factory: TransportFactory;

    constructor(opts: Elm327Options = {}, factory?: TransportFactory) {
        this.opts = {
            serviceUuid:        opts.serviceUuid        ?? HM10_SERVICE_UUID,
            characteristicUuid: opts.characteristicUuid ?? HM10_CHAR_UUID,
            lineBufferMax:      opts.lineBufferMax      ?? 4096,
            pollIntervalMs:     opts.pollIntervalMs     ?? 1000,
            acceptAllDevices:   opts.acceptAllDevices   ?? false,
        };
        this.factory = factory ?? createWebBluetoothTransport;
    }

    on(handler: (e: DriverEvent) => void): () => void {
        this.listeners.push(handler);
        return () => {
            this.listeners = this.listeners.filter((h) => h !== handler);
        };
    }

    private emit(e: DriverEvent): void {
        for (const h of this.listeners) {
            try { h(e); } catch (err) { console.error('elm327 listener error', err); }
        }
    }

    /**
     * Begin a connect flow. Pops the OS chooser via the configured transport
     * factory (Web Bluetooth or Capacitor BLE), opens the notify pipe, runs
     * the ELM327 init sequence, then starts polling.
     *
     * Errors emit a final `status: error` event and resolve to false; the
     * caller can surface the message verbatim.
     */
    async connect(): Promise<boolean> {
        try {
            this.emit({ kind: 'status', status: 'requesting' });
            this.transport = await this.factory(this.opts, (e) => this.emit(e));

            this.transport.onDisconnect(() => {
                this.emit({ kind: 'status', status: 'disconnected' });
                this.stopPolling();
            });

            this.emit({ kind: 'status', status: 'connecting' });
            this.transport.onNotify((bytes) => this.handleBytes(bytes));

            this.emit({ kind: 'status', status: 'initialising' });
            for (const cmd of INIT_COMMANDS) {
                await this.write(cmd);
                // ATZ reset takes 500–1000ms; other AT commands need ~150ms.
                await delay(cmd.startsWith('ATZ') ? 800 : 150);
            }

            this.emit({ kind: 'status', status: 'connected' });
            this.startPolling();
            return true;
        } catch (err) {
            const e = err as Error;
            this.emit({ kind: 'status', status: 'error', message: e.message ?? String(err) });
            return false;
        }
    }

    async disconnect(): Promise<void> {
        this.stopPolling();
        try {
            if (this.transport) await this.transport.disconnect();
        } catch { /* swallow */ }
        finally {
            this.transport = null;
            this.buffer = '';
            this.emit({ kind: 'status', status: 'idle' });
        }
    }

    private async write(cmd: string): Promise<void> {
        if (!this.transport) throw new Error('not_connected');
        const enc = new TextEncoder().encode(cmd);
        await this.transport.write(enc);
    }

    private handleBytes(bytes: Uint8Array): void {
        const text = new TextDecoder().decode(bytes);
        this.buffer += text;
        if (this.buffer.length > this.opts.lineBufferMax) {
            // Cap the buffer if a wedged dongle stops emitting prompts.
            this.buffer = this.buffer.slice(-this.opts.lineBufferMax);
        }
        // Lines are terminated by '>' (ELM327 prompt) or '\r'.
        let promptIdx = this.buffer.indexOf('>');
        while (promptIdx !== -1) {
            const chunk = this.buffer.slice(0, promptIdx);
            this.buffer = this.buffer.slice(promptIdx + 1);
            this.handleChunk(chunk);
            promptIdx = this.buffer.indexOf('>');
        }
    }

    private handleChunk(chunk: string): void {
        // A chunk may contain multiple newline-separated lines.
        // ELM327 sometimes returns multiple responses on one line without newlines,
        // or uses carriage returns (\r) without newlines (\n).
        // We split by \r, \n, or the prompt character > (which we've already mostly handled, but just in case).
        const lines = chunk.split(/[\r\n]+/).map(cleanLine).filter((l) => l.length > 0);
        for (const raw of lines) {
            this.emit({ kind: 'line', raw });
            // Some clones return multiple frames concatenated like "41 0D 00 41 0C 00 00"
            // We'll let the parser handle the first valid one it finds.
            const result = parseLine(raw, Math.floor(Date.now() / 1000));
            this.emit({ kind: 'parsed', result });
        }
    }

    private startPolling(): void {
        if (this.pollHandle !== null) return;
        this.pollHandle = setInterval(() => {
            void this.pollOnce();
        }, this.opts.pollIntervalMs);
    }

    private stopPolling(): void {
        if (this.pollHandle !== null) {
            clearInterval(this.pollHandle);
            this.pollHandle = null;
        }
    }

    private async pollOnce(): Promise<void> {
        if (!this.transport) return;
        const pid = this.pids[this.pidCursor]!;
        this.pidCursor = (this.pidCursor + 1) % this.pids.length;
        try {
            await this.write(PID_QUERIES[pid].cmd);
        } catch (err) {
            console.warn('poll write failed', err);
        }
    }
}

/* ──────────────────────────────────────────────────────────────────
 * Web Bluetooth transport — Chrome on Android.
 * ────────────────────────────────────────────────────────────────── */

async function createWebBluetoothTransport(opts: Required<Elm327Options>, _emit: (e: DriverEvent) => void): Promise<BleTransport> {
    if (!isWebBluetoothSupported()) {
        throw new Error('Web Bluetooth is not available on this device');
    }
    const bt = (navigator as Navigator & {
        bluetooth: {
            requestDevice(opts: {
                filters?: Array<{ services?: string[]; namePrefix?: string }>;
                optionalServices?: string[];
                acceptAllDevices?: boolean;
            }): Promise<BluetoothDevice>;
        };
    }).bluetooth;

    // Two-pronged filter: service UUID OR namePrefix. See the namePrefix
    // list comment in this file for why we need both.
    const allServices = [opts.serviceUuid, ...ALT_SERVICE_UUIDS];
    const requestArgs = opts.acceptAllDevices
        ? { acceptAllDevices: true, optionalServices: allServices }
        : {
            filters: [
                { services: [opts.serviceUuid] },
                ...ELM327_NAME_PREFIXES.map((p) => ({ namePrefix: p })),
            ],
            optionalServices: allServices,
        };
    const device = await bt.requestDevice(requestArgs);
    const server = await device.gatt!.connect();
    const { characteristic } = await discoverObdService(server, opts);
    await characteristic.startNotifications();

    let notifyHandler: (bytes: Uint8Array) => void = () => { /* noop until set */ };
    let disconnectHandler: () => void = () => { /* noop until set */ };

    const onValue = (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        const value = target.value;
        if (!value) return;
        notifyHandler(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    };
    characteristic.addEventListener('characteristicvaluechanged', onValue);
    device.addEventListener('gattserverdisconnected', () => disconnectHandler());

    return {
        async write(bytes) {
            // Cast: TS lib.dom.d.ts in strict mode wants ArrayBuffer not
            // ArrayBufferLike; runtime treats them identically.
            await characteristic.writeValue(bytes as unknown as BufferSource);
        },
        onNotify(handler) {
            notifyHandler = handler;
        },
        onDisconnect(handler) {
            disconnectHandler = handler;
        },
        async disconnect() {
            try { characteristic.removeEventListener('characteristicvaluechanged', onValue); }
            catch { /* swallow */ }
            try { if (device.gatt?.connected) device.gatt.disconnect(); }
            catch { /* swallow */ }
        },
    };
}

function delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
}

/**
 * Walks a connected GATT server looking for an OBD-compatible
 * (notify+write) characteristic. Strategy:
 *
 * 1. Try the configured service + characteristic UUIDs first (fast path
 *    for HM-10 dongles).
 * 2. Try the ALT_SERVICE_UUIDS list with each ALT_CHAR_UUIDS pairing.
 * 3. As a last resort, enumerate ALL services + characteristics on the
 *    device and pick the first that has notify + write properties. This
 *    catches dongles with unusual UUIDs.
 *
 * If nothing works, throws an Error whose message includes the list of
 * services the device DOES expose, so we can add the right UUIDs to the
 * known-list and turn this into a fast path next time.
 */
async function discoverObdService(
    server: BluetoothRemoteGATTServer,
    opts: Required<Elm327Options>,
): Promise<{ service: BluetoothRemoteGATTService; characteristic: BluetoothRemoteGATTCharacteristic }> {
    const serviceCandidates = [opts.serviceUuid, ...ALT_SERVICE_UUIDS];
    const charCandidates = [opts.characteristicUuid, ...ALT_CHAR_UUIDS];

    // Phase 1: known service + known characteristic combinations.
    for (const svcUuid of serviceCandidates) {
        let service: BluetoothRemoteGATTService;
        try {
            service = await server.getPrimaryService(svcUuid);
        } catch {
            continue;
        }
        for (const charUuid of charCandidates) {
            try {
                const characteristic = await service.getCharacteristic(charUuid);
                if (isObdCapableChar(characteristic)) {
                    return { service, characteristic };
                }
            } catch { /* try next characteristic */ }
        }
        // Service exists, but none of the known characteristics matched —
        // enumerate this service's characteristics and pick anything with
        // notify+write properties.
        try {
            const chars = await service.getCharacteristics();
            for (const c of chars) {
                if (isObdCapableChar(c)) {
                    return { service, characteristic: c };
                }
            }
        } catch { /* try next service */ }
    }

    // Phase 2: enumerate ALL services on the device.
    let allServices: BluetoothRemoteGATTService[] = [];
    try { allServices = await server.getPrimaryServices(); } catch { /* swallow */ }
    for (const service of allServices) {
        try {
            const chars = await service.getCharacteristics();
            for (const c of chars) {
                if (isObdCapableChar(c)) {
                    return { service, characteristic: c };
                }
            }
        } catch { /* try next */ }
    }

    // Out of ideas. Surface what the device DOES expose so we can extend
    // the known-list. The user sees this verbatim in the status bar.
    const seen = allServices.map((s) => s.uuid).join(', ');
    const summary = seen.length > 0
        ? `Device exposes services: ${seen}. None had a notify+write characteristic.`
        : 'Device exposed no readable services. Likely a Bluetooth Classic dongle (Web Bluetooth can\'t talk to those).';
    throw new Error(`No OBD-compatible BLE service found on device. ${summary}`);
}

function isObdCapableChar(c: BluetoothRemoteGATTCharacteristic): boolean {
    const p = c.properties;
    return p.notify && (p.write || p.writeWithoutResponse);
}
